/**
 * Regional tourism-statistics filing (Italy ISTAT).
 *
 * The companion family to the per-guest police filing (`registration.ts`,
 * ADR-0046). Where a guest filing reports one guest of one reservation to a
 * police system (Italy Alloggiati, Slovenia eTurizem), a STATISTICS filing
 * reports a property-level AGGREGATE — the day's arrivals, departures and
 * overnight stays — to a regional tourism-statistics office. It has no guest
 * and no reservation: it is one scheduled rollup per (property, region, day).
 *
 * Italy makes this a genuinely separate obligation: Alloggiati covers only the
 * art. 109 TULPS police report, while overnight-stays reporting for the ISTAT
 * survey is filed to a REGIONAL system.
 *
 * Canonical source of truth for the monorepo (same discipline as `tax.ts` /
 * `registration.ts`): the Zod schemas validate at the API boundary and the types
 * are imported by the engine and the mobile collection.
 */

import { z } from "zod";
import type { RegistrationSystem } from "./registration-systems";

// Protocols — the only thing that is ever hard-coded
//
// A statistics destination is reached by a PROTOCOL, and the protocol is what an
// adapter implements. Regions do not each get code: thirteen of them sit behind
// one protocol (Ross 1000, run by GIES for the regions listed below), wearing
// different regional brand names and different hostnames. So the adapter seam is
// keyed by protocol and a region is pure catalog data pointing at one — adding a
// region is a data change, never a migration and never a new adapter.

/** The statistics wire protocols we have (or are building) an adapter for. */
export const statisticsProtocolValues = ["ross1000"] as const;

export type StatisticsProtocol = (typeof statisticsProtocolValues)[number];

// Destination catalog
//
// One entry per statistics DESTINATION: usually a region, occasionally a
// sub-regional office (Toscana's Ross 1000 instance serves only the Florence
// metropolitan city plus the provinces of Pistoia and Prato). Endpoints are
// transcribed verbatim from the GIES "Tracciato XML" specification
// (https://www.ross1000.it/source/tracciato-xml.pdf, "ELENCO END POINT"), minus
// its `?wsdl` suffix. Italian regions absent from that list run other systems
// and are not catalogued until their protocol is known — never a guess.
//
// Every destination is `planned`: the Ross 1000 adapter is built and
// registered, but none is proven yet against a regional service (AGENTS.md,
// "Never wait on production proof").

/** Catalogued statistics destinations, in the specification's own order. */
export const statisticsRegionValues = [
  "it-piemonte",
  "it-toscana-fi-pt-po",
  "it-abruzzo",
  "it-veneto",
  "it-emilia-romagna",
  "it-marche",
  "it-lombardia",
  "it-calabria",
  "it-sardegna",
  "it-liguria",
  "it-basilicata",
  "it-lazio",
  "it-molise",
] as const;

export type StatisticsRegion = (typeof statisticsRegionValues)[number];

/**
 * Integration status of a destination.
 * - `planned`: catalogued, no live adapter — the scheduler never files it.
 * - `live`: its protocol's adapter is registered and the daily scheduler files it.
 * Flipping one to `live` is what a pilot ships (a real adapter + credentials),
 * never a data-only change.
 */
export const statisticsRegionStatusValues = ["planned", "live"] as const;
export type StatisticsRegionStatus =
  (typeof statisticsRegionStatusValues)[number];

/**
 * When the reported period closes relative to the send; each destination's
 * is a registry row (`@vidimo/compliance/data`), beside its deadline.
 * - `one_day`: file yesterday's completed day (the common overnight-stays case).
 * - `this_day`: file the current day.
 */
export const statisticsSendDelayValues = ["one_day", "this_day"] as const;
export type StatisticsSendDelay = (typeof statisticsSendDelayValues)[number];

export interface StatisticsRegionSpec {
  /**
   * The areas it serves, by the official list's area code (Italy's province
   * sigla), so a property's statistics destination follows its municipality.
   */
  areaCodes: readonly string[];
  code: StatisticsRegion;
  /** ISO 3166-1 alpha-2 of the country whose regime this destination belongs to. */
  countryCode: string;
  /** The credential-vault system holding this destination's login. */
  credentialSystem: RegistrationSystem;
  /** SOAP endpoint; append `?wsdl` for the contract. */
  endpoint: string;
  /** Protocol the adapter registry is keyed by. */
  protocol: StatisticsProtocol;
  /** Host-facing name of the destination. */
  regionName: string;
  /**
   * Observed send mode. Only `aggregated` has appeared so far: one report per
   * property-day (which may list guests inside it), never one call per guest.
   */
  sendMode: "aggregated";
  status: StatisticsRegionStatus;
}

function ross1000(
  code: StatisticsRegion,
  regionName: string,
  endpoint: string,
  areaCodes: readonly string[]
): StatisticsRegionSpec {
  return {
    areaCodes,
    code,
    countryCode: "IT",
    credentialSystem: "istat",
    endpoint,
    protocol: "ross1000",
    regionName,
    sendMode: "aggregated",
    status: "planned",
  };
}

export const STATISTICS_REGIONS: Record<
  StatisticsRegion,
  StatisticsRegionSpec
> = {
  "it-abruzzo": ross1000(
    "it-abruzzo",
    "Abruzzo",
    "https://app.regione.abruzzo.it/Turismo5/ws/checkinV2",
    ["AQ", "CH", "PE", "TE"]
  ),
  // Basilicata is still on the older `checkinV1` contract. Its request is the
  // V2 shape minus the booking-only `<cancellazione>` / `<conferma>`
  // corrections, which the adapter never sends, so one builder serves both.
  "it-basilicata": ross1000(
    "it-basilicata",
    "Basilicata",
    "https://sist-aptbasilicata.turitweb.it/ws/checkinV1",
    ["MT", "PZ"]
  ),
  "it-calabria": ross1000(
    "it-calabria",
    "Calabria",
    "https://sirdat.regione.calabria.it/ws/checkinV2",
    ["CS", "CZ", "KR", "RC", "VV"]
  ),
  "it-emilia-romagna": ross1000(
    "it-emilia-romagna",
    "Emilia-Romagna",
    "https://datiturismo.regione.emilia-romagna.it/ws/checkinV2",
    ["BO", "FC", "FE", "MO", "PC", "PR", "RA", "RE", "RN"]
  ),
  "it-lazio": ross1000(
    "it-lazio",
    "Lazio",
    "https://lazioturismo.ross1000.it/ws/checkinV2",
    ["FR", "LT", "RI", "RM", "VT"]
  ),
  // Liguria runs Ross 1000 only for appartamenti ammobiliati ad uso turistico;
  // its hotels and other accommodation file through RIMOVCLI, a protocol not
  // catalogued: https://www.regione.liguria.it/components/com_publiccompetitions/includes/download.php?id=77377:lettera-aaut-2026.pdf
  "it-liguria": ross1000(
    "it-liguria",
    "Liguria (appartamenti ammobiliati ad uso turistico)",
    "https://turismows.regione.liguria.it/ws/checkinV2",
    ["GE", "IM", "SP", "SV"]
  ),
  "it-lombardia": ross1000(
    "it-lombardia",
    "Lombardia",
    "https://www.flussituristici.servizirl.it/Turismo5/app/ws/checkinV2",
    ["BG", "BS", "CO", "CR", "LC", "LO", "MB", "MI", "MN", "PV", "SO", "VA"]
  ),
  "it-marche": ross1000(
    "it-marche",
    "Marche",
    "https://istrice-ross1000.turismo.marche.it/ws/checkinV2",
    ["AN", "AP", "FM", "MC", "PU"]
  ),
  "it-molise": ross1000(
    "it-molise",
    "Molise",
    "https://moliseturismo.ross1000.it/ws/checkinV2",
    ["CB", "IS"]
  ),
  "it-piemonte": ross1000(
    "it-piemonte",
    "Piemonte",
    "https://piemontedatiturismo.regione.piemonte.it/ws/checkinV2",
    ["AL", "AT", "BI", "CN", "NO", "TO", "VB", "VC"]
  ),
  "it-sardegna": ross1000(
    "it-sardegna",
    "Sardegna",
    "https://sardegnaturismo.ross1000.it/ws/checkinV2",
    ["CA", "CI", "NU", "OG", "OR", "OT", "SS", "VS"]
  ),
  "it-toscana-fi-pt-po": ross1000(
    "it-toscana-fi-pt-po",
    "Toscana (Firenze, Pistoia, Prato)",
    "https://toscanaturismo.ross1000.it/turismo5-web/ws/checkinV2",
    ["FI", "PO", "PT"]
  ),
  "it-veneto": ross1000(
    "it-veneto",
    "Veneto",
    "https://flussituristici.regione.veneto.it/ws/checkinV2",
    ["BL", "PD", "RO", "TV", "VE", "VI", "VR"]
  ),
};

/**
 * Look a destination up by its stored key. The column is free-form text, so a
 * row can name a destination this build does not know (a rollback, a catalog
 * entry removed); callers must treat `undefined` as "cannot file" rather than
 * crash.
 */
export function statisticsRegionSpec(
  code: string
): StatisticsRegionSpec | undefined {
  return STATISTICS_REGIONS[code as StatisticsRegion];
}

/** Destinations whose protocol adapter is live and which are therefore actually
 * filed. Empty until a pilot flips one. */
export function liveStatisticsRegions(): StatisticsRegion[] {
  return statisticsRegionValues.filter(
    (code) => STATISTICS_REGIONS[code].status === "live"
  );
}

// Report kind
//
// What the aggregate reports. `overnight_stays` (arrivals + presences, the
// ISTAT "movimento" report) is the one in scope; kept an enum so occupancy or
// arrivals-only variants can join without a schema change to the row shape.

export const statisticsReportKindValues = ["overnight_stays"] as const;
export const StatisticsReportKindSchema = z.enum(statisticsReportKindValues);

// Stay detail
//
// What a per-guest protocol (Ross 1000 wants one anonymous profile per arriving
// guest) needs beyond the counts, snapshotted with them. Protocol-neutral: raw
// ISO codes and our own vocabulary, never a wire code; the adapter translates
// and decides what is missing. Server-only: the payload never syncs to clients.

/**
 * One guest of one stay, as the statistics report sees them. Profile fields
 * are optional because a capture can be incomplete; the adapter decides which
 * absences block a report.
 */
export const statisticsStayGuestSchema = z.object({
  /** Check-in calendar date of the stay, `YYYY-MM-DD`. */
  arrivalDate: z.string(),
  birthCountry: z.string().optional(),
  birthPlace: z.string().optional(),
  bookingSource: z.string().optional(),
  /** ISO nationality, alpha-2 or alpha-3 (the MRZ emits alpha-3). */
  citizenship: z.string().optional(),
  dateOfBirth: z.string().optional(),
  /** Check-out calendar date of the stay, `YYYY-MM-DD`. */
  departureDate: z.string(),
  /**
   * Who the host reads in an error ("Maria Rossi"). Never sent on the wire:
   * the statistics path is anonymous.
   */
  displayName: z.string(),
  /** Whether this guest heads the party (a lone guest heads a party of one). */
  isPartyHead: z.boolean(),
  /** Family or group, from this guest's own captured stay purpose. */
  partyKind: z.enum(["family", "group"]),
  /** Stable per reservation; members point at their head through it. */
  partyRef: z.string(),
  partySize: z.number().int().positive(),
  residenceAddress: z.string().optional(),
  /** ISO 3166-1 alpha-2. */
  residenceCountry: z.string().optional(),
  sex: z.enum(["M", "F"]).optional(),
  stayPurpose: z.string().optional(),
  /** Stable per (reservation, guest): the report's record id for this person. */
  stayRef: z.string(),
});
export type StatisticsStayGuest = z.infer<typeof statisticsStayGuestSchema>;

/** One calendar day of the reported period. */
export const statisticsDayDetailSchema = z.object({
  /** Guests whose stay began this day. */
  arrivals: z.array(statisticsStayGuestSchema),
  /** `YYYY-MM-DD`. */
  date: z.string(),
  /** Guests whose stay ended this day. */
  departures: z.array(statisticsStayGuestSchema),
  /** Distinct units occupied this night. */
  unitsOccupied: z.number().int().nonnegative(),
  /**
   * People counted on reservations arriving this day (adults + children) with
   * no linked guest record to report them by.
   */
  unrecordedArrivals: z.number().int().nonnegative(),
  /**
   * Arrivals an earlier send of this day reported that are no longer this
   * day's (the stay was cancelled, or its check-in moved). A resend must
   * withdraw them; leaving them out would leave the office's copy standing.
   */
  withdrawals: z.array(statisticsStayGuestSchema).optional(),
});
export type StatisticsDayDetail = z.infer<typeof statisticsDayDetailSchema>;

export const statisticsStayDetailSchema = z.object({
  /**
   * Sum of the units' maximum guests; null when any unit has none set, so a
   * capacity is never under-reported by guessing.
   */
  bedsAvailable: z.number().int().nonnegative().nullable(),
  days: z.array(statisticsDayDetailSchema),
  unitsAvailable: z.number().int().nonnegative(),
});
export type StatisticsStayDetail = z.infer<typeof statisticsStayDetailSchema>;

// Aggregate snapshot
//
// The computed rollup a filing reports, snapshotted onto the durable row at
// enqueue so processing never needs a live recomputation (a host retry
// recomputes it, so a corrected guest record reaches the report). The counts
// are common to every overnight-stays regime; `detail` carries the per-day,
// per-guest breakdown a protocol like Ross 1000 needs.

export const statisticsAggregateSchema = z.object({
  /** Guests who checked in during the period. */
  arrivals: z.number().int().nonnegative(),
  /** Guests who checked out during the period. */
  departures: z.number().int().nonnegative(),
  /** Absent on rows snapshotted before the detail existed. */
  detail: statisticsStayDetailSchema.optional(),
  /** Guest-nights (presences): guests × nights spent inside the period. */
  guestNights: z.number().int().nonnegative(),
  /** Occupied unit-nights: distinct units occupied × nights inside the period. */
  occupiedUnitNights: z.number().int().nonnegative(),
  /** Last day of the reported period (inclusive), ISO date `YYYY-MM-DD`. */
  periodEnd: z.string(),
  /** First day of the reported period (inclusive), ISO date `YYYY-MM-DD`. */
  periodStart: z.string(),
});
export type StatisticsAggregate = z.infer<typeof statisticsAggregateSchema>;
